/* ============================================================
LUMIA 7.0 – Header.js  (Part 1 of 3)
============================================================
Includes Popup, Wishlist, Account, QuickView, Search, StickyHeader,
and base utilities.  MegaMenu (click-to-open) will appear in Part 2.
============================================================ */

const mobileBreakpoint = 1024;

// ---------- Utility: Debounce ----------
function debounce(fn, delay) {
  let t;
  return function (...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), delay);
  };
}

// ---------- Base Popup Class ----------
class Popup extends HTMLElement {
  constructor() {
    super();
    this.popupSelector = this.dataset.selector;
    this.cloneSelector = document.querySelector(this.dataset.clone);
    this.popupElement = document.querySelector(this.popupSelector);
    this.popupLinks = this.querySelectorAll("a, span");
    this.cachedResults = [];

    this.buttonTemplate = `
      <button type="button" data-fancybox-close class="fancybox-button fancybox-close-small" title="Close">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
          <path d="M13 12l5-5-1-1-5 5-5-5-1 1 5 5-5 5 1 1 5-5 5 5 1-1z"/>
        </svg>
      </button>`;

    // choose trigger
    if (this.dataset.event === "mouseenter") {
      this.popupLinks.forEach((link) => {
        let timer;
        link.addEventListener("mouseenter", (e) => {
          timer = setTimeout(() => this.open(e), 250);
        });
        link.addEventListener("mouseleave", () => clearTimeout(timer));
      });
    } else {
      this.popupLinks.forEach((link) => link.addEventListener("click", this.open.bind(this)));
    }

    window.addEventListener(
      "resize",
      debounce(() => {
        if (this.isOpened()) this.isScroll();
      }, 400)
    );
  }

  open(e) {
    if (e) e.preventDefault();
    if (this.isOpened()) return;
    const anim = this.dataset.animationEffect || "fade";

    jq_lumia.fancybox.open({
      type: "inline",
      src: this.popupSelector,
      animationEffect: anim,
      animationDuration: 200,
      touch: false,
      afterLoad: () => {
        bodyScrollLock.disableBodyScroll(this.popupElement, {
          allowTouchMove: (el) => {
            while (el && el !== document.body) {
              if (el.getAttribute("body-scroll-lock-ignore") !== null) return true;
              el = el.parentElement;
            }
          },
        });

        this.popupElement.classList.add("is-opened");
        this.updateScroll();
      },
      beforeClose: () => {
        this.popupElement.classList.remove("is-opened");
        bodyScrollLock.enableBodyScroll(this.popupElement);
      },
      afterClose: () => {
        document.body.classList.remove("blockSticky");
      },
    });
  }

  isOpened() {
    return !!this.popupElement.closest(".fancybox-is-open");
  }

  updateScroll() {
    const scrollElems = this.popupElement.querySelectorAll(".js-dropdn-content-scroll");
    scrollElems.forEach((el) => {
      if (!Scrollbar.get(el)) {
        Scrollbar.init(el, { alwaysShowTracks: true });
      } else {
        Scrollbar.get(el).update();
      }
    });
  }
}

// ---------- Minicart Popup ----------
class MinicartPopup extends Popup {
  constructor() {
    super();
  }
  popupAfterShow() {
    this.updateScroll();
  }
}

// ---------- Wishlist Popup ----------
class WishlistPopup extends Popup {
  constructor() {
    super();
  }
}

// ---------- Account Popup ----------
class AccountPopup extends Popup {
  constructor() {
    super();
  }
}

// ---------- Quick View Popup ----------
class QuickViewPopup extends Popup {
  constructor() {
    super();
  }
  open(e) {
    e.preventDefault();
    const url = e.currentTarget.getAttribute("href");
    jq_lumia.fancybox.open({
      src: url,
      type: "ajax",
      animationEffect: "fade",
      afterLoad: (instance, current) => {
        const product = current.$content[0];
        product.classList.add("quickview-loaded");
      },
    });
  }
}

// ---------- Search Popup ----------
class SearchPopup extends Popup {
  constructor() {
    super();
  }
}

// ---------- Sticky Header ----------
class HeaderSticky extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector(".hdr");
    this.clone = this.header.cloneNode(true);
    this.clone.classList.add("hdr--sticky");
    document.body.appendChild(this.clone);
    window.addEventListener("scroll", this.onScroll.bind(this));
  }

  onScroll() {
    if (window.scrollY > 100) {
      this.clone.classList.add("is-visible");
    } else {
      this.clone.classList.remove("is-visible");
    }
  }
}

/* ============================================================
   LUMIA 7.0 – Header.js  (Part 2 of 3)
============================================================
Rewritten MegaMenu Class
Click-to-open behavior replaces hover-based dropdowns.
============================================================ */

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.menu = this.querySelector(".mmenu-js");
    this.initEvents();
    document.addEventListener("click", this.handleOutsideClick.bind(this));
  }

  initEvents() {
    this.menuItems = this.querySelectorAll(".mmenu > li");

    this.menuItems.forEach((item) => {
      const link = item.querySelector("a");
      const submenu = item.querySelector(".mmenu-submenu, .megamenu, ul");

      if (!link) return;

      // Remove existing hover handlers
      item.removeEventListener("mouseenter", this.onLinkMouseEnter);
      item.removeEventListener("mouseleave", this.onLinkMouseLeave);

      // Replace with click-based open/close logic
      link.addEventListener("click", (e) => {
        if (!submenu) return; // no submenu → normal link

        // If user is trying to open in new tab/window, don't interfere
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

        // 1st click: open dropdown (unchanged)
        if (!item.classList.contains("hovered")) {
          e.preventDefault();
          this.closeAll();
          item.classList.add("hovered");
          this.setMaxHeight(submenu);
          return;
        }

        // 2nd click: navigate to the header link (collection)
        // Another script is currently preventing default + closing the dropdown.
        // Force navigation and stop other click handlers from running.
        const href = link.getAttribute("href");
        if (href && href !== "#" && href !== "javascript:void(0)" && href !== "javascript:;") {
          e.preventDefault();
          e.stopImmediatePropagation();
          window.location.assign(href);
          return;
        }
      });
    });
  }

  closeAll() {
    this.menuItems.forEach((i) => {
      i.classList.remove("hovered");
      const s = i.querySelector(".mmenu-submenu, .megamenu, ul");
      if (s) this.clearMaxHeight(s);
    });
  }

  handleOutsideClick(e) {
    if (!this.contains(e.target)) {
      this.closeAll();
    }
  }

  setMaxHeight(submenu) {
    submenu.style.display = "block";
    submenu.style.overflow = "hidden";
    submenu.style.maxHeight = "0px";
    submenu.style.transition = "max-height 0.3s ease";

    requestAnimationFrame(() => {
      const fullHeight = submenu.scrollHeight + "px";
      submenu.style.maxHeight = fullHeight;
    });
  }

  clearMaxHeight(submenu) {
    submenu.style.transition = "max-height 0.3s ease";
    submenu.style.maxHeight = "0px";

    setTimeout(() => {
      submenu.style.display = "";
      submenu.style.overflow = "";
    }, 300);
  }
}

/* ---------- CSS Override for click menus ---------- */
const styleTag = document.createElement("style");
styleTag.textContent = `
  .hdr-nav .mmenu > li:hover > .mmenu-submenu,
  .hdr-nav .mmenu > li:hover > .megamenu,
  .hdr-nav .mmenu > li:hover > ul {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  .hdr-nav .mmenu > li.hovered > .mmenu-submenu,
  .hdr-nav .mmenu > li.hovered > .megamenu,
  .hdr-nav .mmenu > li.hovered > ul {
    display: block !important;
    opacity: 1 !important;
    visibility: visible !important;
  }

  .mmenu-submenu, .megamenu, .hdr-nav ul ul {
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease;
  }
`;
document.head.appendChild(styleTag);

/* ============================================================
   LUMIA 7.0 – Header.js  (Part 3 of 3)
============================================================
Initializes Popups, Sticky Header, MegaMenu, and Search logic
============================================================ */

// ---------- Init Components ----------
window.addEventListener("DOMContentLoaded", () => {
  // Init Popups
  // ❌ Disabled — minicart handled by cart.js + <mini-cart-popup>
  // document.querySelectorAll('popup-minicart').forEach(el => new MinicartPopup());
  document.querySelectorAll("popup-wishlist").forEach((el) => new WishlistPopup());
  document.querySelectorAll("popup-account").forEach((el) => new AccountPopup());
  document.querySelectorAll("popup-quickview").forEach((el) => new QuickViewPopup());
  document.querySelectorAll("popup-search").forEach((el) => new SearchPopup());

  // Init Sticky Header
  if (document.querySelector("header-sticky")) {
    new HeaderSticky();
  }

  // Init MegaMenu (click-to-open)
  var mm = document.querySelector("mega-menu");
  if (mm && typeof mm.init === "function") mm.init();

  // Predictive Search handling (if Lumia has built-in API)
  const predictiveInput = document.querySelector("#LiveSearchInput");
  const predictiveResults = document.querySelector("#LiveSearchResults");

  if (predictiveInput && predictiveResults) {
    let delay;
    predictiveInput.addEventListener("input", () => {
      clearTimeout(delay);

      const q = predictiveInput.value.trim();
      if (q.length < 2) {
        predictiveResults.innerHTML = "";
        return;
      }

      delay = setTimeout(() => {
        fetch(
          `/search/suggest.json?q=${encodeURIComponent(
            q
          )}&resources[type]=product&resources[limit]=5&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor`
        )
          .then((res) => res.json())
          .then((data) => {
            const products = data.resources?.results?.products || [];
            if (!products.length) {
              predictiveResults.innerHTML = `<div class="predictive-search__empty">No results found</div>`;
              return;
            }

            predictiveResults.innerHTML = products
              .map(
                (p) => `
                  <a href="${p.url}" class="predictive-search__item">
                    ${
                      p.featured_image?.url
                        ? `<img src="${p.featured_image.url}" class="predictive-search__image" alt="${p.title}">`
                        : ""
                    }
                    <span class="predictive-search__title">${p.title}</span>
                  </a>
                `
              )
              .join("");
          })
          .catch((err) => console.error("Predictive search error:", err));
      }, 250);
    });

    document.addEventListener("click", (e) => {
      if (!predictiveResults.contains(e.target) && e.target !== predictiveInput) {
        predictiveResults.innerHTML = "";
      }
    });
  }
});

/* ==========================================================
   VPP FIX: Load AJAX "show-on-scroll" homepage sections
   - Fills empty divs with data-ajax-section-scroll
   - Does NOT touch header/menu/dropdowns
   ========================================================== */
(function () {
  function loadOne(el) {
    const url = el.getAttribute("data-ajax-section-scroll");
    if (!url || el.__vppLoaded) return;
    el.__vppLoaded = true;

    fetch(url, { credentials: "same-origin" })
      .then((r) => r.text())
      .then((html) => {
        const tmp = document.createElement("div");
        tmp.innerHTML = html;

        // Prefer returning the inner .shopify-section wrapper content
        const section = tmp.querySelector(".shopify-section");
        if (section) el.innerHTML = section.innerHTML;
        else el.innerHTML = html;

        // Let other theme scripts know something loaded (optional)
        document.dispatchEvent(new CustomEvent("vpp:lazysection:loaded", { detail: { el } }));
        document.dispatchEvent(new CustomEvent("shopify:section:load"));
      })
      .catch((err) => {
        console.error("VPP lazy section load failed:", url, err);
        el.__vppLoaded = false;
      });
  }

  function loadAll() {
    document
      .querySelectorAll(
        '[data-ajax-section-scroll].shopify-section, [data-ajax-section-scroll].show-on-scroll'
      )
      .forEach(loadOne);
  }

  document.addEventListener("DOMContentLoaded", loadAll);
  window.addEventListener("load", loadAll);

  // Load again on scroll (some themes only load when visible)
  window.addEventListener("scroll", loadAll, { passive: true });

  // If theme replaces content, try again
  const mo = new MutationObserver(loadAll);
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
